import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';

import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AlarmChangedEvent } from '../../shared/events/alarm-changed.event';
import { AttributesChangedEvent } from '../../shared/events/attributes-changed.event';
import { BlindsChangedEvent } from '../../shared/events/blinds-changed.event';
import { ButtonPressedEvent } from '../../shared/events/button-pressed.event';
import { CurtainChangedEvent } from '../../shared/events/cutains-changed.event';
import { FanSpeedChangedEvent } from '../../shared/events/fanspeed-changed.event';
import { LightChangedEvent } from '../../shared/events/light-changed.event';
import { MediaStateChangedEvent } from '../../shared/events/media-state-changed.event';
import { ReservationStateChangedEvent } from '../../shared/events/reservation-state-changed.event';
import { RoomStateChangedEvent } from '../../shared/events/room-state-changed.event';
import { TemperatureChangedEvent } from '../../shared/events/temperature-changed.event';
import { AlarmActionTypes } from '../../sub-features/room/alarm/alarm-store/alarm.actions';
import { AttributesActionTypes } from '../../sub-features/room/attributes/attributes-store/attributes.actions';
import { BlindActionTypes } from '../../sub-features/room/blinds/blinds-store/blinds.actions';
import { CurtainActionTypes } from '../../sub-features/room/blinds/curtains-store/curtains.actions';
import { FanspeedActionTypes } from '../../sub-features/room/fanspeed/fanspeed-store/fanspeed.actions';
import { LightsActionTypes } from '../../sub-features/room/lights/lights-store/lights.actions';
import { MediaActionTypes } from '../../sub-features/room/media/media.actions';
import { TemperatureActionTypes } from '../../sub-features/room/temperature/temperature-store/temperature.actions';
import { ButtonActionTypes } from '../buttons/buttons.action';
import { MobileReservationActionTypes } from '../mobilereservation/mobilereservation.actions';
import { RoomActionTypes } from '../rooms/rooms.actions';
import {
  AlarmChanged,
  AttributesChanged,
  BlindsChanged,
  ButtonPressed,
  CurtainChanged,
  FanSpeedChanged,
  LightChanged,
  MediaStateChanged,
  ReservationStateChanged,
  RoomStateChanged,
  TemperatureChanged,
} from './messages.actions';

@Injectable()
export class SignalrEffects {
  private readonly actions$ = inject(Actions);

  alarmUpdateSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AlarmActionTypes.ALARM_SIGNALR_STATE_CHANGE),
      switchMap((action: { payload: AlarmChangedEvent }) => of(new AlarmChanged(action.payload)))
    );
  });

  updateMediaState$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MediaActionTypes.MEDIA_SIGNALR_STATE_CHANGED),
      switchMap((action: { payload: { mediaState: MediaStateChangedEvent } }) =>
        of(new MediaStateChanged(action.payload.mediaState))
      )
    );
  });

  fanspeedUpdateSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(FanspeedActionTypes.UPDATE_FANSPEED),
      switchMap((action: { payload: FanSpeedChangedEvent }) => of(new FanSpeedChanged(action.payload)))
    );
  });

  temperatureUpdateSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TemperatureActionTypes.UPDATE_TEMPERATURE),
      switchMap((action: { payload: TemperatureChangedEvent }) => of(new TemperatureChanged(action.payload)))
    );
  });

  buttonPressedSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ButtonActionTypes.BUTTON_SIGNALR_STATE_CHANGED),
      switchMap((action: { payload: ButtonPressedEvent }) => of(new ButtonPressed(action.payload)))
    );
  });

  curtainUpdateSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CurtainActionTypes.UPDATE_CURTAIN),
      switchMap((action: { payload: CurtainChangedEvent }) => of(new CurtainChanged(action.payload)))
    );
  });

  blindUpdateSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BlindActionTypes.UPDATE_BLIND),
      switchMap((action: { payload: BlindsChangedEvent }) => of(new BlindsChanged(action.payload)))
    );
  });

  lightsUpdateSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LightsActionTypes.UPDATE_LIGHTS),
      switchMap((action: { payload: LightChangedEvent }) => of(new LightChanged(action.payload)))
    );
  });

  roomUpdateSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_SIGNALR_STATE_CHANGE),
      switchMap((action: { payload: RoomStateChangedEvent }) => of(new RoomStateChanged(action.payload)))
    );
  });

  reservationUpdateSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(MobileReservationActionTypes.MOBILE_RESERVATION_SIGNALR_STATE_CHANGED),
      switchMap((action: { payload: ReservationStateChangedEvent }) => of(new ReservationStateChanged(action.payload)))
    );
  });

  attributesUpdateSignalR$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AttributesActionTypes.ATTRIBUTES_SIGNALR_STATE_CHANGED),
      switchMap((action: { payload: AttributesChangedEvent }) => of(new AttributesChanged(action.payload)))
    );
  });
}
